import { can, formatDate } from '@clim/client-core';
import {
  DISCOUNT_KIND_LABELS,
  DISCOUNT_KINDS,
  STOCK_INDICATOR_LABELS,
  UNIT_LABELS,
  currentPrice,
  discountHtFromTtc,
  compareAtTtcOf,
  htOfTtc,
  priceTtcOf,
  formatEuros,
  lineTotals,
  missingForSale,
  ttcCents,
  type CatalogItemRecord,
  type DiscountKind,
  type DossierLineRecord,
  type DossierRecord,
  type StockIndicator,
} from '@clim/domain';
import { useId, useMemo, useState } from 'react';
import { errorMessage } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';
import { Empty, ErrorBox, Field, Input, Modal, SyncTag } from './ui';

const power = (i: CatalogItemRecord) =>
  i.powerMinBtu == null
    ? null
    : i.powerMinBtu === i.powerMaxBtu || i.powerMaxBtu == null
      ? `${i.powerMinBtu.toLocaleString('fr-FR')} BTU`
      : `${i.powerMinBtu.toLocaleString('fr-FR')}–${i.powerMaxBtu.toLocaleString('fr-FR')} BTU`;

/** Produits / prestations du dossier (CDC §4.6) : catalogue dynamique, quantités, options dépliables, totaux. */
export function DossierLines({ dossier, editable }: { dossier: DossierRecord; editable: boolean }) {
  const engine = useEngine();
  const { me } = useReady();
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lines = engine
    .list('dossier_line')
    .filter((l) => l.dossierId === dossier.id)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt));
  const totals = engine.totals(dossier.id);
  // chantier partenaire (sous-traitance de pose) : pas de vente, donc aucun prix ; la puissance choisie fixe la
  // rémunération du poseur
  const partnerJob = !!dossier.partnerId;
  // technicien : il voit ce qu'il doit poser, sans aucun montant
  const showPrices = !partnerJob && lines.every((l) => l.unitPriceHtCents !== null);
  const [discounting, setDiscounting] = useState<DossierLineRecord | null>(null);
  const [pricing, setPricing] = useState<DossierLineRecord | null>(null);
  const canPrice = can(me, 'dossier.price.override');
  // administrateur : changer le modèle d'un appareil à tout moment (rupture fournisseur…), prix inchangé
  const canSubstitute = can(me, 'dossier.line.substitute') && dossier.status !== 'cancelled';
  const [substituting, setSubstituting] = useState<DossierLineRecord | null>(null);
  const run = (p: Promise<unknown>) => p.catch((e) => setError(errorMessage(e)));

  return (
    <div className="card">
      <div className="card-head">
        <h2>{partnerJob ? 'Puissance & prestations à poser' : 'Produits & prestations'}</h2>
        {editable && can(me, 'catalog.read') && (
          <button className="btn small primary" onClick={() => setPicking(true)}>
            {partnerJob ? '+ Choisir la puissance' : '+ Ajouter'}
          </button>
        )}
      </div>
      {dossier.signature && <p className="muted">🔒 Contrat signé : produits figés.</p>}
      {lines.length === 0 ? (
        <Empty>
          {partnerJob
            ? 'Choisissez la puissance de la climatisation (et les suppléments éventuels) : la rémunération du poseur suit le barème du catalogue.'
            : 'Aucun produit. Ajoutez au moins un produit ou une prestation avant de soumettre le dossier.'}
        </Empty>
      ) : (
        <ul className="plain lines">
          {lines.map((l) => {
            const t = lineTotals({
              ...l,
              unitPriceHtCents: l.unitPriceHtCents ?? 0,
              discountHtCents: l.discountHtCents ?? 0,
            });
            return (
              <li key={l.id}>
                <div className="line-head">
                  <div className="row-main">
                    <strong>{l.label}</strong>
                    {l.substitution && (
                      <span className="substitution small" data-testid="substitution">
                        🔄 Modèle changé le {formatDate(l.substitution.at)} par {l.substitution.byName} — avant : {l.substitution.fromLabel} ·{' '}
                        {l.substitution.reason}
                      </span>
                    )}
                    {canSubstitute && (
                      <button type="button" className="link small" onClick={() => setSubstituting(l)}>
                        🔄 Changer de modèle
                      </button>
                    )}
                    <span className="muted">
                      {l.model && `${l.model}${showPrices ? ' · ' : ''}`}
                      {showPrices && `${formatEuros(l.unitPriceHtCents)} HT / ${UNIT_LABELS[l.unit]}`}
                    </span>
                    {showPrices && (
                      <span className="catalog-price">
                        <strong>
                          {formatEuros(l.unitPriceTtcCents ?? ttcCents(l.unitPriceHtCents ?? 0, l.vatRateBp))}{' '}
                          TTC
                        </strong>
                        {l.compareAtPriceHtCents != null &&
                          l.compareAtPriceHtCents > (l.unitPriceHtCents ?? 0) && (
                            <s className="muted">
                              {' '}
                              {formatEuros(
                                l.compareAtPriceTtcCents ?? ttcCents(l.compareAtPriceHtCents, l.vatRateBp),
                              )}
                            </s>
                          )}
                        {l.priceOverridden && <span className="muted"> · prix propre à ce dossier</span>}
                      </span>
                    )}
                  </div>
                  {editable ? (
                    <div className="qty" aria-label={`Quantité ${l.label}`}>
                      <button
                        className="btn small"
                        aria-label="Diminuer"
                        disabled={l.quantity <= 1}
                        onClick={() => void run(engine.updateLine(l.id, { quantity: l.quantity - 1 }))}
                      >
                        −
                      </button>
                      <span>{l.quantity}</span>
                      <button
                        className="btn small"
                        aria-label="Augmenter"
                        onClick={() => void run(engine.updateLine(l.id, { quantity: l.quantity + 1 }))}
                      >
                        +
                      </button>
                    </div>
                  ) : (
                    <span>× {l.quantity}</span>
                  )}
                  {showPrices && <strong className="nowrap">{formatEuros(t.htCents)}</strong>}
                  {editable && showPrices && canPrice && (
                    <button
                      className="btn small"
                      onClick={() => setPricing(l)}
                      aria-label={`Prix ${l.label}`}
                    >
                      ✎ Prix
                    </button>
                  )}
                  {editable && showPrices && (
                    <button
                      className="btn small"
                      onClick={() => setDiscounting(l)}
                      aria-label={`Remise ${l.label}`}
                    >
                      {l.discountHtCents ? 'Remise ✎' : '% Remise'}
                    </button>
                  )}
                  {editable && (
                    <button
                      className="btn small ghost"
                      aria-label={`Retirer ${l.label}`}
                      onClick={() => void run(engine.removeLine(l.id))}
                    >
                      ✕
                    </button>
                  )}
                </div>
                {!!l.discountHtCents && l.discountKind && (
                  <div className="discount-tag">
                    Remise − {formatEuros(l.discountTtcCents ?? ttcCents(l.discountHtCents, l.vatRateBp))} TTC
                    · {DISCOUNT_KIND_LABELS[l.discountKind]}
                    {l.discountNote && ` · ${l.discountNote}`}
                  </div>
                )}
                {l.quoteHighlight && <span className="badge highlight-badge">Mis en avant sur le devis</span>}
                {l.optionSteps.length > 0 && (
                  <details className="steps">
                    <summary>Voir les {l.optionSteps.length} étapes</summary>
                    <ol>
                      {l.optionSteps.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ol>
                  </details>
                )}
                {(l.saleCommissionCents != null || l.installRemunerationCents != null) && (
                  <div className="muted small">
                    {l.saleCommissionCents != null &&
                      !partnerJob &&
                      `Commission : ${formatEuros(l.saleCommissionCents)}`}
                    {l.saleCommissionCents != null &&
                      !partnerJob &&
                      l.installRemunerationCents != null &&
                      ' · '}
                    {l.installRemunerationCents != null &&
                      `Rémunération pose : ${formatEuros(l.installRemunerationCents)}`}
                    {l.frozenAt &&
                      (partnerJob ? ' (figée à l’envoi en planification)' : ' (figées à la validation)')}
                  </div>
                )}
                <SyncTag state={engine.syncState('dossier_line', l.id)} />
              </li>
            );
          })}
        </ul>
      )}
      {lines.length > 0 && showPrices && (
        <dl className="totals">
          <dt>Total HT</dt>
          <dd>{formatEuros(totals.htCents)}</dd>
          <dt>TVA</dt>
          <dd>{formatEuros(totals.vatCents)}</dd>
          <dt>Total TTC</dt>
          <dd>
            <strong>{formatEuros(totals.ttcCents)}</strong>
          </dd>
        </dl>
      )}
      {/* options liées : ex. l'extension de garantie 5 ans exige le contrat d'entretien 5 ans */}
      {engine.requirementsOf(dossier.id).map((m) => (
        <p key={m} className="field-error" role="alert">
          ⚠ {m}
        </p>
      ))}
      <ErrorBox message={error} />
      {picking && <ItemPicker dossier={dossier} onClose={() => setPicking(false)} />}
      {discounting && <DiscountDialog line={discounting} onClose={() => setDiscounting(null)} />}
      {pricing && <PriceDialog line={pricing} onClose={() => setPricing(null)} />}
      {substituting && <SubstituteDialog dossier={dossier} line={substituting} onClose={() => setSubstituting(null)} />}
    </div>
  );
}

/**
 * Administrateur : remplacer le modèle d'un appareil (rupture de stock du fournisseur…), même après signature tant que
 * la facture n'est pas transmise. Prix, remise, quantité et commissions restent ceux signés ; motif obligatoire.
 */
function SubstituteDialog({ dossier, line, onClose }: { dossier: DossierRecord; line: DossierLineRecord; onClose(): void }) {
  const engine = useEngine();
  const { api } = useReady();
  const brands = engine.list('catalog_brand');
  const current = engine.get('catalog_item', line.itemId);
  const [q, setQ] = useState('');
  const [itemId, setItemId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const brandOf = (id: string | null) => (id ? brands.find((b) => b.id === id)?.label : undefined);
  const items = useMemo(() => {
    const term = q.trim().toLowerCase();
    return engine
      .list('catalog_item')
      .filter((i) => i.isActive && i.id !== line.itemId && (!current || i.sectionId === current.sectionId))
      .filter((i) => !i.dossierTypeIds.length || i.dossierTypeIds.includes(dossier.dossierTypeId))
      .filter((i) => !term || [i.label, i.model, brandOf(i.brandId)].some((v) => v?.toLowerCase().includes(term)))
      // même puissance d'abord (le remplaçant naturel)
      .sort(
        (a, b) =>
          Number(b.powerMinBtu === current?.powerMinBtu) - Number(a.powerMinBtu === current?.powerMinBtu) ||
          a.sortOrder - b.sortOrder ||
          (a.powerMinBtu ?? 0) - (b.powerMinBtu ?? 0),
      )
      .slice(0, 40);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, engine.version, q, line.itemId, dossier.dossierTypeId]);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.request('POST', `/v1/lines/${line.id}/substitute`, { itemId, reason: reason.trim() });
      await engine.sync();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Changer de modèle" onClose={onClose}>
      <div className="form">
        <p>
          Actuel : <strong>{line.label}</strong>
          {line.model ? ` — ${line.model}` : ''}
        </p>
        <p className="muted small">
          Le prix, la remise, la quantité et les commissions restent ceux du bon de commande signé. L’ancien modèle et le motif sont conservés
          (journal du dossier) ; le commercial et le poseur sont prévenus. Impossible une fois la facture transmise à la comptabilité.
        </p>
        <Field label="Rechercher le nouveau modèle">
          <Input type="search" aria-label="Rechercher le nouveau modèle" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Marque, modèle…" />
        </Field>
        <ul className="list plain substitute-list">
          {items.map((i) => (
            <li key={i.id}>
              <label className={`check row${itemId === i.id ? ' on' : ''}`}>
                <input type="radio" name="substitute" checked={itemId === i.id} onChange={() => setItemId(i.id)} />{' '}
                <span className="row-main">
                  <strong>{[i.label, brandOf(i.brandId)].filter(Boolean).join(' – ')}</strong>
                  <span className="muted">{[i.model, i.energyClass, i.warranty].filter(Boolean).join(' · ') || 'modèle à compléter dans le catalogue'}</span>
                </span>
              </label>
            </li>
          ))}
          {!items.length && <Empty>Aucun autre modèle actif dans cette rubrique.</Empty>}
        </ul>
        <Field label="Motif (obligatoire)">
          <Input aria-label="Motif du changement de modèle" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. : rupture de stock chez le fournisseur" />
        </Field>
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button className="btn primary" disabled={!itemId || reason.trim().length < 3 || busy} onClick={() => void save()}>
            Remplacer le modèle
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ItemPicker({ dossier, onClose }: { dossier: DossierRecord; onClose(): void }) {
  const engine = useEngine();
  // chantier partenaire : choix de la puissance (barème de pose), même un article pas encore ouvert à la vente ;
  // ni prix, ni remise
  const partnerJob = !!dossier.partnerId;
  const sections = engine
    .list('catalog_section')
    .filter((s) => s.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const [sectionId, setSectionId] = useState(sections[0]?.id ?? '');
  const [q, setQ] = useState('');
  const [qty, setQty] = useState<Record<string, number>>({});
  const [disc, setDisc] = useState<Record<string, DiscountDraft>>({});
  const { me } = useReady();
  const canPrice = can(me, 'dossier.price.override');
  const [prices, setPrices] = useState<Record<string, PriceDraft>>({});
  const [error, setError] = useState<string | null>(null);
  const brands = engine.list('catalog_brand');
  const families = engine.list('catalog_family');
  const items = useMemo(() => {
    const term = q.trim().toLowerCase();
    return engine
      .list('catalog_item')
      .filter((i) => (i.isActive || partnerJob) && i.sectionId === sectionId)
      .filter((i) => !i.dossierTypeIds.length || i.dossierTypeIds.includes(dossier.dossierTypeId))
      .filter((i) => !i.territoryIds.length || i.territoryIds.includes(dossier.territoryId))
      .filter(
        (i) =>
          !term ||
          [i.label, i.model, brands.find((b) => b.id === i.brandId)?.label].some((v) =>
            v?.toLowerCase().includes(term),
          ),
      )
      .map((i) => ({ item: i, price: currentPrice(i.prices, dossier.territoryId) }))
      .filter((x) => x.price)
      .sort(
        (a, b) =>
          a.item.sortOrder - b.item.sortOrder || (a.item.powerMinBtu ?? 0) - (b.item.powerMinBtu ?? 0),
      );
  }, [engine, engine.version, sectionId, q, dossier.dossierTypeId, dossier.territoryId, brands, partnerJob]);

  const add = async (itemId: string, vatRateBp: number) => {
    try {
      const p = toPrice(prices[itemId], vatRateBp);
      if (p && 'error' in p) throw new Error(p.error);
      if (partnerJob) await engine.addLine(dossier.id, itemId, qty[itemId] ?? 1);
      else
        await engine.addLine(
          dossier.id,
          itemId,
          qty[itemId] ?? 1,
          null,
          toDiscount(disc[itemId], vatRateBp),
          p,
        );
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <Modal
      title={partnerJob ? 'Puissance / prestation à poser' : 'Ajouter un produit / une prestation'}
      onClose={onClose}
    >
      <div className="form">
        <div className="chips">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`chip${sectionId === s.id ? ' on' : ''}`}
              onClick={() => setSectionId(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <Input
          type="search"
          placeholder="Rechercher (libellé, marque, modèle)…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Rechercher dans le catalogue"
        />
        {items.length === 0 ? (
          <Empty>Aucun article disponible ici.</Empty>
        ) : (
          <ul className="plain">
            {items.map(({ item, price }) => {
              const brand = brands.find((b) => b.id === item.brandId)?.label;
              const missing = partnerJob ? [] : missingForSale(item);
              return (
                <li key={item.id} className="row picker-row catalog-card">
                  {item.imageUrl && (
                    <img className="catalog-thumb" src={item.imageUrl} alt="" loading="lazy" />
                  )}
                  <div className="row-main">
                    <strong>{item.label}</strong>
                    {brand && <span className="catalog-brand">{brand}</span>}
                    {item.model && <span>Modèle : {item.model}</span>}
                    {item.energyClass && <span>Classe : {item.energyClass}</span>}
                    {item.warranty && <span>Garantie : {item.warranty}</span>}
                    {!brand && !item.model && (
                      <span className="muted">
                        {[families.find((f) => f.id === item.familyId)?.label, power(item)]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    )}
                    {partnerJob ? (
                      <span className="muted">
                        {[
                          power(item),
                          price!.installRemunerationCents != null &&
                            `Rémunération pose : ${formatEuros(price!.installRemunerationCents)} / ${UNIT_LABELS[item.unit]}`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    ) : (
                      <span className="catalog-price">
                        <strong>{formatEuros(priceTtcOf(price!))} TTC</strong>
                        {price!.compareAtPriceHtCents != null &&
                          price!.compareAtPriceHtCents > price!.priceHtCents && (
                            <s className="muted"> {formatEuros(compareAtTtcOf(price!)!)}</s>
                          )}
                        <span className="muted">
                          {' '}
                          · {formatEuros(price!.priceHtCents)} HT / {UNIT_LABELS[item.unit]}
                        </span>
                      </span>
                    )}
                    {item.description && <span className="muted">{item.description}</span>}
                    {item.requiresItemIds.length > 0 && (
                      <span className="muted">
                        Avec :{' '}
                        {item.requiresItemIds
                          .map((rid) => engine.get('catalog_item', rid)?.label)
                          .filter(Boolean)
                          .join(' ou ')}{' '}
                        (ajouté d’office)
                      </span>
                    )}
                    {missing.length > 0 && (
                      <span className="field-error">Variante incomplète : {missing.join(', ')}</span>
                    )}
                    <StockChip indicator={item.stock?.[dossier.territoryId]} />
                  </div>
                  <Field label="Qté">
                    <Input
                      type="number"
                      min={0.5}
                      step={item.unit === 'meter' ? 0.5 : 1}
                      value={qty[item.id] ?? 1}
                      onChange={(e) => setQty({ ...qty, [item.id]: Number(e.target.value) })}
                      style={{ width: 80 }}
                    />
                  </Field>
                  {canPrice &&
                    !partnerJob &&
                    (prices[item.id] ? (
                      <PriceFields
                        value={prices[item.id]!}
                        onChange={(v) => setPrices({ ...prices, [item.id]: v })}
                        onRemove={() =>
                          setPrices(Object.fromEntries(Object.entries(prices).filter(([k]) => k !== item.id)))
                        }
                      />
                    ) : (
                      <button
                        className="btn small"
                        disabled={missing.length > 0}
                        onClick={() =>
                          setPrices({
                            ...prices,
                            [item.id]: {
                              ttc: centsText(priceTtcOf(price!)),
                              oldTtc:
                                price!.compareAtPriceHtCents != null
                                  ? centsText(compareAtTtcOf(price!)!)
                                  : '',
                            },
                          })
                        }
                        aria-label={`Prix pour ce dossier ${[item.label, brand, item.model].filter(Boolean).join(' ')}`}
                      >
                        ✎ Prix
                      </button>
                    ))}
                  {partnerJob ? null : disc[item.id] ? (
                    <DiscountFields
                      value={disc[item.id]!}
                      onChange={(v) => setDisc({ ...disc, [item.id]: v })}
                      onRemove={() =>
                        setDisc(Object.fromEntries(Object.entries(disc).filter(([k]) => k !== item.id)))
                      }
                    />
                  ) : (
                    <button
                      className="btn small"
                      disabled={missing.length > 0}
                      onClick={() => setDisc({ ...disc, [item.id]: { ttc: '', kind: null, note: '' } })}
                      aria-label={`Remise sur ${[item.label, brand, item.model].filter(Boolean).join(' ')}`}
                    >
                      % Remise
                    </button>
                  )}
                  <button
                    className="btn primary small"
                    disabled={missing.length > 0}
                    onClick={() => void add(item.id, price!.vatRateBp)}
                    aria-label={`Ajouter ${[item.label, brand, item.model].filter(Boolean).join(' ')}`}
                  >
                    Ajouter
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <ErrorBox message={error} />
      </div>
    </Modal>
  );
}

const STOCK_TONES: Record<StockIndicator, string> = { available: 'success', low: 'warning', out: 'danger' };
/** §13 : le commercial voit la disponibilité, jamais les quantités ; la vente n'est jamais bloquée. */
export function StockChip({ indicator }: { indicator: StockIndicator | undefined }) {
  if (!indicator) return null;
  return <span className={`badge tone-${STOCK_TONES[indicator]}`}>{STOCK_INDICATOR_LABELS[indicator]}</span>;
}

/* ------------------------------------------------------------------ remise */

interface DiscountDraft {
  /** montant TTC saisi (ce que le client voit), en euros */
  ttc: string;
  kind: DiscountKind | null;
  note: string;
}

const euros = (v: string) => Math.round(Number(v.replace(',', '.').replace(/\s/g, '')) * 100);

function toDiscount(d: DiscountDraft | undefined, vatRateBp: number) {
  if (!d || !d.ttc.trim()) return undefined;
  const raw = euros(d.ttc);
  const cents = Number.isFinite(raw) && raw > 0 ? raw : 0;
  return {
    discountHtCents: cents ? discountHtFromTtc(cents, vatRateBp) : 0,
    discountTtcCents: cents || null,
    discountKind: d.kind,
    discountNote: d.note.trim() || null,
  };
}

/** Remise au choix du produit : montant TTC, puis « remise commerciale » ou « accord dirigeant / responsable ». */
function DiscountFields({
  value,
  onChange,
  onRemove,
}: {
  value: DiscountDraft;
  onChange(v: DiscountDraft): void;
  onRemove?(): void;
}) {
  const group = useId();
  return (
    <fieldset className="discount-box">
      <legend>Remise</legend>
      <Field label="Montant de la remise (€ TTC)">
        <Input
          inputMode="decimal"
          value={value.ttc}
          onChange={(e) => onChange({ ...value, ttc: e.target.value })}
          placeholder="ex. 50"
        />
      </Field>
      {DISCOUNT_KINDS.map((k) => (
        <label key={k} className="check">
          <input
            type="radio"
            name={group}
            checked={value.kind === k}
            onChange={() => onChange({ ...value, kind: k })}
          />
          {DISCOUNT_KIND_LABELS[k]}
        </label>
      ))}
      {value.kind === 'management' && (
        <>
          <Field label="Accord donné par (facultatif)">
            <Input
              value={value.note}
              onChange={(e) => onChange({ ...value, note: e.target.value })}
              maxLength={300}
            />
          </Field>
          <p className="muted small">L’administrateur sera averti de cette remise.</p>
        </>
      )}
      {value.kind === 'commercial' && (
        <p className="muted small">Le montant HT de la remise sera déduit de votre commission.</p>
      )}
      {onRemove && (
        <button type="button" className="link" onClick={onRemove}>
          Pas de remise
        </button>
      )}
    </fieldset>
  );
}

function DiscountDialog({ line, onClose }: { line: DossierLineRecord; onClose(): void }) {
  const engine = useEngine();
  const [value, setValue] = useState<DiscountDraft>({
    ttc: line.discountHtCents
      ? String((line.discountTtcCents ?? ttcCents(line.discountHtCents, line.vatRateBp)) / 100).replace(
          '.',
          ',',
        )
      : '',
    kind: line.discountKind,
    note: line.discountNote ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const save = (d: {
    discountHtCents: number;
    discountKind: DiscountKind | null;
    discountNote: string | null;
    discountTtcCents?: number | null;
  }) =>
    engine
      .updateLine(line.id, d)
      .then(onClose)
      .catch((e) => setError(errorMessage(e)));
  return (
    <Modal title={`Remise — ${line.label}`} onClose={onClose}>
      <div className="form">
        <DiscountFields value={value} onChange={setValue} />
        <ErrorBox message={error} />
        <div className="actions">
          {!!line.discountHtCents && (
            <button
              className="btn danger-outline"
              onClick={() =>
                void save({
                  discountHtCents: 0,
                  discountKind: null,
                  discountNote: null,
                  discountTtcCents: null,
                })
              }
            >
              Retirer la remise
            </button>
          )}
          <button className="btn" onClick={onClose}>
            Annuler
          </button>
          <button
            className="btn primary"
            onClick={() =>
              void save(
                toDiscount(value, line.vatRateBp) ?? {
                  discountHtCents: 0,
                  discountKind: null,
                  discountNote: null,
                  discountTtcCents: null,
                },
              )
            }
          >
            Enregistrer la remise
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ prix propre au dossier (administrateur) */

interface PriceDraft {
  /** prix TTC pour ce dossier, en euros */
  ttc: string;
  /** ancien prix TTC affiché barré (facultatif) */
  oldTtc: string;
}

const centsText = (c: number) => (c / 100).toFixed(2).replace('.', ',');

/** Conversion des montants TTC saisis en HT (TTC exact retrouvé) ; erreur lisible si invalide. */
function toPrice(d: PriceDraft | undefined, vatRateBp: number) {
  if (!d) return undefined;
  const ttc = euros(d.ttc);
  if (!Number.isFinite(ttc) || ttc < 0) return { error: 'Prix du dossier invalide' };
  const old = d.oldTtc.trim() ? euros(d.oldTtc) : null;
  if (old !== null && (!Number.isFinite(old) || old <= ttc))
    return { error: 'L’ancien prix barré doit être supérieur au prix' };
  return {
    unitPriceHtCents: htOfTtc(ttc, vatRateBp),
    compareAtPriceHtCents: old === null ? null : htOfTtc(old, vatRateBp),
    unitPriceTtcCents: ttc,
    compareAtPriceTtcCents: old,
  };
}

/** Administrateur : prix TTC de ce produit pour CE dossier seulement, et ancien prix barré. */
function PriceFields({
  value,
  onChange,
  onRemove,
}: {
  value: PriceDraft;
  onChange(v: PriceDraft): void;
  onRemove?(): void;
}) {
  return (
    <fieldset className="discount-box">
      <legend>Prix pour ce dossier</legend>
      <Field label="Prix (€ TTC)">
        <Input
          inputMode="decimal"
          value={value.ttc}
          onChange={(e) => onChange({ ...value, ttc: e.target.value })}
        />
      </Field>
      <Field label="Ancien prix barré (€ TTC, facultatif)">
        <Input
          inputMode="decimal"
          value={value.oldTtc}
          onChange={(e) => onChange({ ...value, oldTtc: e.target.value })}
        />
      </Field>
      <p className="muted small">Le catalogue ne change pas : ce prix ne vaut que pour ce dossier.</p>
      {onRemove && (
        <button type="button" className="link" onClick={onRemove}>
          Prix du catalogue
        </button>
      )}
    </fieldset>
  );
}

function PriceDialog({ line, onClose }: { line: DossierLineRecord; onClose(): void }) {
  const engine = useEngine();
  const [value, setValue] = useState<PriceDraft>({
    ttc: centsText(line.unitPriceTtcCents ?? ttcCents(line.unitPriceHtCents ?? 0, line.vatRateBp)),
    oldTtc:
      line.compareAtPriceHtCents != null
        ? centsText(line.compareAtPriceTtcCents ?? ttcCents(line.compareAtPriceHtCents, line.vatRateBp))
        : '',
  });
  const [error, setError] = useState<string | null>(null);
  const save = () => {
    const p = toPrice(value, line.vatRateBp);
    if (!p || 'error' in p) return setError(p?.error ?? null);
    void engine
      .updateLine(line.id, {
        unitPriceHtCents: p.unitPriceHtCents,
        compareAtPriceHtCents: p.compareAtPriceHtCents,
        unitPriceTtcCents: p.unitPriceTtcCents,
        compareAtPriceTtcCents: p.compareAtPriceTtcCents,
      })
      .then(onClose)
      .catch((e) => setError(errorMessage(e)));
  };
  return (
    <Modal title={`Prix — ${line.label}`} onClose={onClose}>
      <div className="form">
        <PriceFields value={value} onChange={setValue} />
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Annuler
          </button>
          <button className="btn primary" onClick={save}>
            Enregistrer le prix
          </button>
        </div>
      </div>
    </Modal>
  );
}
